import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Search, Building2, ArrowRight, FileText, Star, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const OfferChoice = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleZlecamPrace = () => {
    // If user is logged in, go to new offer form
    // If not, go to login first
    if (user) {
      navigate('/new-offer');
    } else {
      navigate('/login?redirect=/new-offer');
    }
  };

  const handlePromujFirme = () => {
    // Check if user is already a company
    if (user?.user_type === 'company') {
      navigate('/dashboard');
    } else if (user) {
      // User is logged in but not a company - show info about switching
      navigate('/register?type=company&upgrade=true');
    } else {
      navigate('/register?type=company');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900" data-testid="offer-choice-page">
      {/* Header */}
      <div className="bg-slate-900/50 border-b border-slate-700">
        <div className="max-w-5xl mx-auto px-4 py-6">
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-white text-center">
            Co chcesz zrobić?
          </h1>
          <p className="text-slate-400 text-center mt-2">
            Wybierz opcję dopasowaną do Twoich potrzeb
          </p>
        </div>
      </div>

      {/* Choice Cards */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          
          {/* Option 1: Zlecam pracę */}
          <Card 
            className="bg-slate-800/50 border-2 border-slate-700 hover:border-orange-500 transition-all duration-300 cursor-pointer group"
            onClick={handleZlecamPrace}
            data-testid="choice-zlecam-prace"
          >
            <CardContent className="p-8">
              <div className="flex flex-col items-center text-center">
                {/* Icon */}
                <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-amber-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Search className="w-10 h-10 text-white" />
                </div>
                
                {/* Title */}
                <h2 className="font-heading font-bold text-2xl text-white mb-3">
                  Zlecam pracę
                </h2>
                
                {/* Description */}
                <p className="text-slate-400 mb-6">
                  Szukasz wykonawcy do remontu, budowy lub naprawy? 
                  Dodaj ogłoszenie i otrzymaj oferty od sprawdzonych firm.
                </p>
                
                {/* Benefits */}
                <div className="space-y-3 text-left w-full mb-6">
                  <div className="flex items-center gap-3 text-slate-300">
                    <FileText className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span className="text-sm">Opisz swoją potrzebę</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <Users className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span className="text-sm">Firmy wyślą Ci oferty</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <Star className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <span className="text-sm">Wybierz najlepszą propozycję</span>
                  </div>
                </div>
                
                {/* CTA */}
                <Button 
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6 group-hover:bg-orange-600"
                  data-testid="btn-zlecam-prace"
                >
                  Dodaj ogłoszenie
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Option 2: Promuj firmę */}
          <Card 
            className="bg-slate-800/50 border-2 border-slate-700 hover:border-blue-500 transition-all duration-300 cursor-pointer group"
            onClick={handlePromujFirme}
            data-testid="choice-promuj-firme"
          >
            <CardContent className="p-8">
              <div className="flex flex-col items-center text-center">
                {/* Icon */}
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Building2 className="w-10 h-10 text-white" />
                </div>
                
                {/* Title */}
                <h2 className="font-heading font-bold text-2xl text-white mb-3">
                  Promuj firmę
                </h2>
                
                {/* Description */}
                <p className="text-slate-400 mb-6">
                  Masz firmę budowlaną? Stwórz profesjonalny profil 
                  i docieraj do klientów szukających wykonawców.
                </p>
                
                {/* Benefits */}
                <div className="space-y-3 text-left w-full mb-6">
                  <div className="flex items-center gap-3 text-slate-300">
                    <Building2 className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-sm">Stwórz profil z logo i opisem</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <Star className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-sm">Zbieraj opinie od klientów</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-300">
                    <Search className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <span className="text-sm">Klienci sami Cię znajdą</span>
                  </div>
                </div>
                
                {/* CTA */}
                <Button 
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-6 group-hover:bg-blue-600"
                  data-testid="btn-promuj-firme"
                >
                  {user?.user_type === 'company' ? 'Przejdź do profilu' : 'Załóż profil firmy'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Already registered info */}
        {!user && (
          <div className="text-center mt-8">
            <p className="text-slate-400">
              Masz już konto?{' '}
              <a href="/login" className="text-orange-500 hover:text-orange-400 font-medium">
                Zaloguj się
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfferChoice;
